import CableIcon from "@mui/icons-material/Cable";
import { Box, IconButton } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { formatIsoDate, formatIsoDateTime } from "../../utils/dateUtils";
import type { ExternalTimeOff } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { LabeledField } from "../UIToolkit/molecules/LabeledField";
import SystemDialog from "../UIToolkit/Popups/Dialog";

type Props = { externalTimeOff?: ExternalTimeOff };

/**
 * Support-only indicator marking a PTO that was synced from an external HRIS source.
 * Renders nothing for manually-entered PTOs (no linked external row) or for non-support users.
 * Hovering shows the source name; clicking opens a modal with the full raw external_time_off record.
 */
export const ExternalTimeOffIndicator: FC<Props> = ({ externalTimeOff }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const isSupport = !!userInfo?.support;
	const [open, setOpen] = useState(false);

	if (!externalTimeOff || !isSupport) return null;

	return (
		<>
			<InHouseTooltip title={t("tooltip.pto.external", { source: externalTimeOff.source })}>
				<IconButton
					size="small"
					onClick={() => setOpen(true)}
					aria-label={t("tooltip.pto.external", { source: externalTimeOff.source })}
					data-testid={`external-time-off-indicator-${externalTimeOff.id}`}
					sx={{ p: "2px" }}
				>
					<CableIcon sx={{ width: "15px", height: "15px", color: "primary.dark" }} />
				</IconButton>
			</InHouseTooltip>
			<SystemDialog
				open={open}
				onClose={() => setOpen(false)}
				title={t("pto.external.modal.title")}
				hideCancel
				confirm={t("close")}
				onConfirm={() => setOpen(false)}
				dataTestId="external-time-off-dialog"
				body={
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
							rowGap: "16px",
							columnGap: "24px",
							pt: 1,
						}}
					>
						<LabeledField label={t("profile.integrations.field.source")} value={externalTimeOff.source} />
						<LabeledField label={t("profile.integrations.field.externalId")} value={externalTimeOff.external_employee_id} />
						<LabeledField label={t("type")} value={externalTimeOff.type} />
						<LabeledField label={t("start.date")} value={formatIsoDate(externalTimeOff.start_date)} />
						<LabeledField label={t("end.date")} value={formatIsoDate(externalTimeOff.end_date)} />
						<LabeledField label={t("hours")} value={externalTimeOff.hours} />
						<LabeledField label={t("reason")} value={externalTimeOff.reason} />
						<LabeledField label={t("profile.integrations.field.syncKey")} value={externalTimeOff.sync_key} />
						<LabeledField label={t("profile.integrations.field.createdAt")} value={formatIsoDateTime(externalTimeOff.created_at)} />
						<LabeledField label={t("profile.integrations.field.updatedAt")} value={formatIsoDateTime(externalTimeOff.updated_at)} />
					</Box>
				}
			/>
		</>
	);
};
